import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { usePageMeta } from "@/lib/meta";
import Reveal, { Overline } from "@/components/Reveal";
import CtaBand from "@/components/home/CtaBand";

const CREDENTIALS = [
  "Founded and sold Strum Music School in Austin, Texas",
  "Creator of online singing courses with 75,000 students worldwide",
  "BA in Mass Media Communication, The Ohio State University",
];

const TRAINING = [
  "30 years of professional voice and music instruction",
  "Professional musician and published songwriter",
];

const TESTIMONIALS = [1, 2, 3];

export default function About() {
  usePageMeta(
    "About the Consultant — Executive Voice Coach in Austin, TX | Credible Presence",
    "About the Executive Voice Consultant behind Credible Presence: a vocal-mechanics specialist and leadership communication coach serving executives, attorneys, and founders in Austin, TX and virtually."
  );

  return (
    <div data-testid="about-page">
      <section className="relative overflow-hidden bg-[#F7F5F1] pt-40 pb-20 sm:pt-48 sm:pb-28">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <Reveal>
            <Overline>About the Consultant</Overline>
            <h1 className="mt-6 max-w-3xl font-serif text-4xl leading-[1.15] tracking-tight text-[#26282E] sm:text-5xl lg:text-6xl">
              The craft of <em className="text-[#2F5D8C]">commanding attention.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#5B5E66] sm:text-lg">
              Credible Presence is a private executive voice and communication coaching practice based in Austin,
              Texas — serving clients in person and virtually, worldwide.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#E3D9C8] py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 sm:px-8 lg:grid-cols-5 lg:gap-20 lg:px-12">
          <Reveal className="lg:col-span-2">
            <div
              data-testid="about-photo"
              className="card-shadow-lg relative aspect-[4/5] overflow-hidden rounded-md bg-white lg:sticky lg:top-32"
            >
              <img
                src="/steve-glazer.png"
                alt="Steve Glazer, Executive Voice Consultant and founder of Credible Presence"
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#26282E]/95 via-[#26282E]/40 to-transparent p-6 pt-16">
                <p className="font-serif text-xl italic text-[#F7F5F1]">Steve Glazer</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[#E3D9C8]">
                  Executive Voice Consultant
                </p>
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-3">
            <Reveal>
              <Overline>In My Own Words</Overline>
              <div className="mt-6 space-y-6 text-base leading-relaxed text-[#26282E]/80 sm:text-lg" data-testid="about-bio">
                <p>
                  I am <span className="text-[#2F5D8C]">Steve Glazer</span>, an Executive Voice
                  Consultant. I help leaders sound the way their experience deserves — clear, composed, and
                  credible — in the moments that decide careers, capital, and cases.
                </p>
                <p>
                  Before Credible Presence, I spent 30 years as a professional musician, music school founder,
                  and voice coach — reaching more than 75,000 students worldwide. Today I apply that voice
                  expertise to help CEOs speak with presence, authority, and credibility.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <h2 className="mt-16 font-serif text-2xl leading-[1.2] tracking-tight text-[#26282E] sm:text-3xl">
                From vocal performance to the boardroom
              </h2>
              <div className="mt-6 space-y-6 text-base leading-relaxed text-[#5B5E66]">
                <p>
                  My training began in vocal performance — the disciplined study of how the voice is built,
                  supported, and sustained.
                </p>
                <p>
                  Over time, the people asking for my help were not performers. They were executives preparing for
                  board presentations, attorneys heading into trial, and founders walking into investor meetings.
                  The stakes were the same as any stage — and the craft of the voice was the missing piece. That is
                  why Credible Presence exists: executive communication coaching grounded in real vocal mechanics,
                  delivered in business terms.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <h2 className="mt-16 font-serif text-2xl leading-[1.2] tracking-tight text-[#26282E] sm:text-3xl">
                The method: vocal mechanics
              </h2>
              <div className="mt-6 space-y-6 text-base leading-relaxed text-[#5B5E66]">
                <p>
                  Most communication coaching works on what you say. I work on the instrument that says it.
                  Articulation, breath support, resonance, projection, pacing, and stamina are physical skills —
                  trainable, measurable, and reliable under pressure.
                </p>
                <p>
                  You will not be asked to perform or to become someone louder. You will learn precise, sustainable
                  adjustments that let your natural voice carry your authority — in the boardroom, the courtroom,
                  and the investor meeting.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-16">
                <Overline>Credentials &amp; Training</Overline>
                <ul className="mt-6 space-y-3" data-testid="about-credentials">
                  {[...CREDENTIALS, ...TRAINING].map((c, i) => (
                    <li
                      key={i}
                      className="border-l-2 border-[#2F5D8C]/40 pl-4 font-mono text-[11px] uppercase tracking-[0.18em] text-[#5B5E66]"
                    >
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-[#26282E] py-24 sm:py-32" data-testid="about-testimonials">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <Reveal>
            <Overline dark>Client Words</Overline>
            <h2 className="mt-6 font-serif text-3xl leading-[1.2] tracking-tight text-[#F7F5F1] sm:text-4xl">
              Testimonials
            </h2>
            <p className="mt-4 max-w-xl text-sm text-[#8A8D94]">
              Client names and identifying details are withheld to protect confidentiality.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3 lg:gap-8">
            {TESTIMONIALS.map((n) => (
              <Reveal key={n} delay={n * 0.1} className="h-full">
                <figure
                  data-testid={`testimonial-placeholder-${n}`}
                  className="flex h-full flex-col justify-between rounded-md bg-[#2E3038] p-8 transition-transform duration-500 hover:-translate-y-1"
                >
                  <blockquote className="font-serif text-xl italic leading-relaxed text-[#C4BFB4]">
                    “[Testimonial placeholder — a short client quote about the outcome of coaching.]”
                  </blockquote>
                  <figcaption className="mt-8 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8A8D94]">
                    [Client role — placeholder]
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/contact"
              data-testid="about-cta-link"
              className="group mt-14 inline-flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8FB0D4] transition-colors hover:text-[#A8C2DE]"
            >
              Request a Confidential Consultation
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
